import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api-services/api.service';
import { UnleashClient } from 'unleash-proxy-client';
import { UnleashFeatureFlags } from 'src/app/interfaces/interfaces';
import { UnleashService } from '../unleash/unleash.service';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private apiService = inject(ApiService);
  private unleashService = inject(UnleashService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private alertController = inject(AlertController);
  private translateService = inject(TranslateService);
  private deviceToken: string | null = null;
  private installationId: string | null = null;
  private cachedPreferences: string[] | null = null;
  unleashFeatureFlags = UnleashFeatureFlags;

  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await this.createNotificationChannels();
    await this.addListeners();
    await this.registerNotifications();
  }
  private async createNotificationChannels(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    await PushNotifications.createChannel({
      id: 'high_priority',
      name: 'Important Notifications',
      description: 'High priority notifications that require immediate attention',
      importance: 5, // IMPORTANCE_HIGH
      visibility: 1, // VISIBILITY_PUBLIC (1 = public, 0 = private, -1 = secret)
      vibration: true,
      lights: true,
      sound: 'default',
    });

    await PushNotifications.createChannel({
      id: 'default',
      name: 'General Notifications',
      description: 'Default notifications',
      importance: 3, // IMPORTANCE_DEFAULT
      visibility: 0, // VISIBILITY_PRIVATE
      vibration: true,
    });
  }

  private async addListeners(): Promise<void> {
    await PushNotifications.addListener('registration', async (token: Token) => {
      this.deviceToken = token.value;
      await this.registerDeviceWithBackend(token.value);
    });

    await PushNotifications.addListener('registrationError', (error) => {
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        const data = notification.notification.data;
        const action = data?.action as string;
        if (!action) return;

        // ngZone is needed because the listener is outside of Angular's zone
        // and we need to ensure that navigation happens within the Angular context
        // so that it not fails to navigate
        this.ngZone.run(async () => {
          if (action === 'checkoutRequest') {
            const externalId = data?.bookingExternalId as string;
            if (externalId) {
              await this.presentCheckoutRequest(
                externalId,
                notification.notification.body,
              );
            }
            return;
          }

          if (this.unleashService.isEnabled(this.unleashFeatureFlags.ABILITY_TO_DISABLE_PUSH_NOTIFICATION_NAVIGATION)) {
          if (this.isNewsAction(action)) {
            this.router.navigate(['/tabs/social-wall']);
          } else if (this.isBookingAction(action)) {
            const segment = this.isInvitationAction(action) ? 'invitations' : 'mine';
            const bookingId = data?.bookingId as string;
            this.router.navigate(['/tabs/bookings'], {
              queryParams: { segment, ...(bookingId && { bookingId }) },
            });
          }
        }
        });
      }
    );
  }

  private async registerNotifications(): Promise<void> {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      return;
    }

    await PushNotifications.register();
  }

  private async registerDeviceWithBackend(pushToken: string): Promise<void> {
    const platform = Capacitor.getPlatform();
    this.installationId = this.getOrCreateInstallationId();

    const deviceInstallation = {
      installationId: this.installationId,
      platform: (platform === 'ios' ? 'apns' : 'fcmv1') as 'apns' | 'fcmv1',
      pushChannel: pushToken,
      tags: [] as string[],
    };

    try {
      await firstValueFrom(
        this.apiService.registerPushNotificationDevice(deviceInstallation)
      );
    } catch (error) {
      console.error('Failed to register device with backend:', error);
    }
  }

  private getOrCreateInstallationId(): string {
    const storageKey = 'push_installation_id';
    let installationId = localStorage.getItem(storageKey);
    if (!installationId) {
      installationId = crypto.randomUUID();
      localStorage.setItem(storageKey, installationId);
    }
    return installationId;
  }

  getDeviceToken(): string | null {
    return this.deviceToken;
  }

  getInstallationId(): string | null {
    return this.installationId;
  }

  async getPreferences(): Promise<string[]> {
    if (!this.installationId) return [];
    if (this.cachedPreferences) return this.cachedPreferences;

    this.cachedPreferences = await firstValueFrom(
      this.apiService.getPushNotificationPreferences(this.installationId)
    );
    return this.cachedPreferences;
  }

  async savePreferences(tags: string[]): Promise<void> {
    if (!this.installationId || !this.deviceToken) return;
    const platform = Capacitor.getPlatform();
    await firstValueFrom(
      this.apiService.registerPushNotificationDevice({
        installationId: this.installationId,
        platform: (platform === 'ios' ? 'apns' : 'fcmv1') as 'apns' | 'fcmv1',
        pushChannel: this.deviceToken,
        tags,
      })
    );
    this.cachedPreferences = tags;
  }

  async unregisterDevice(): Promise<void> {
    if (!this.installationId) {
      return;
    }

    const installationId = this.installationId;
    this.installationId = null;

    try {
      await firstValueFrom(
        this.apiService.unregisterPushNotificationDevice(installationId)
      );
    } catch (error) {
      console.error('Failed to unregister device from backend:', error);
    }
  }

  async removeAllListeners(): Promise<void> {
    await PushNotifications.removeAllListeners();
  }

  private isBookingAction(action: string): boolean {
    const bookingActions = [
      'accepted', 'rejected', 'cancelled',
      'online_accepted', 'series_accepted',
      'booking.series.cancelled',
    ];
    return this.isInvitationAction(action) || bookingActions.includes(action);
  }

  private isInvitationAction(action: string): boolean {
    return action.startsWith('invitation.') || action === 'series.cancelled';
  }

  private isNewsAction(action: string): boolean {
    return action === 'news';
  }

  private async presentCheckoutRequest(
    externalId: string,
    message?: string,
  ): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translateService.instant('checkoutRequest.title'),
      message,
      mode: 'ios',
      backdropDismiss: false,
      buttons: [
        {
          text: this.translateService.instant('checkoutRequest.ignore'),
          role: 'cancel',
        },
        {
          text: this.translateService.instant('checkOut'),
          role: 'confirm',
          handler: () => {
            void this.approveCheckoutRequest(externalId);
          },
        },
      ],
    });

    await alert.present();
  }

  private async approveCheckoutRequest(externalId: string): Promise<void> {
    try {
      await firstValueFrom(this.apiService.approveCheckoutRequest(externalId));
    } catch {
      const alert = await this.alertController.create({
        header: this.translateService.instant('checkoutRequest.errorTitle'),
        message: this.translateService.instant('checkOutError'),
        mode: 'ios',
        buttons: ['OK'],
      });
      await alert.present();
    }
  }
}
